import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  ViewChild,
  HostListener,
} from '@angular/core';
import { Router } from '@angular/router';
import {
  loadStripe,
  Stripe,
  StripeCardElement,
  PaymentRequest,
  StripePaymentRequestButtonElement,
} from '@stripe/stripe-js';
import { AnalyticsService } from '../../services/analytics.service';
import { CartItem, CartService, OrderType } from '../../services/cart.service';

@Component({
  selector: 'app-payment',
  standalone: false,
  templateUrl: './payment.component.html',
  styleUrl: './payment.component.scss',
})
export class PaymentComponent implements AfterViewInit, OnDestroy {
  @ViewChild('prbHost', { static: false }) prbHost?: ElementRef<HTMLDivElement>;
  @ViewChild('cardHost', { static: false })
  cardHost?: ElementRef<HTMLDivElement>;

  paymentRequest?: PaymentRequest;
  prButton?: StripePaymentRequestButtonElement;
  walletAvailable = false;
  totalAmount = 0;

  stripe: Stripe | null = null;
  card!: StripeCardElement;

  clientSecret!: string;
  publishableKey = '';
  connectedAccount?: string;

  // UI state
  cardComplete = false;
  cardError = '';
  isPaying = false;

  isInitialisingPayment = true;
  paymentReady = false;
  paymentInitMessage = 'Preparing secure payment...';

  private cardMounted = false;

  // Analytics state
  private paymentPageOpenedAt = Date.now();
  private paymentFormReadyAt?: number;
  private hasTrackedCardFocus = false;
  private hasTrackedCardStarted = false;
  private hasTrackedCardCompleted = false;
  private hasClickedPay = false;
  private hasSuccessfulPayment = false;
  private hasTrackedAbandon = false;
  private leaveReason:
    | 'unknown'
    | 'back_to_checkout'
    | 'browser_close_or_refresh' = 'unknown';
  private shouldTrackPaymentSession = false;
  private lastCardValidationError = '';

  summaryItems: CartItem[] = [];
  orderNotes = '';
  orderType: OrderType = 'collection';
  summaryDeliveryFee = 0;
  summaryServiceFee = 0;
  summarySmallOrderFee = 0;
  summaryTotal = 0;

  constructor(
    public router: Router,
    private cartService: CartService,
    private analytics: AnalyticsService,
    private cdr: ChangeDetectorRef,
  ) {
    const nav = this.router.currentNavigation();
    const state =
      (nav?.extras.state as {
        clientSecret?: string;
        publishableKey?: string;
        connectedAccount?: string;
        totalAmount?: number;
      }) || {};

    this.totalAmount =
      state.totalAmount ?? Number(sessionStorage.getItem('totalAmount') || 0);

    sessionStorage.setItem('totalAmount', String(this.totalAmount));

    this.clientSecret =
      state.clientSecret || sessionStorage.getItem('clientSecret') || '';
    this.publishableKey =
      state.publishableKey || sessionStorage.getItem('publishableKey') || '';
    this.connectedAccount =
      state.connectedAccount ||
      sessionStorage.getItem('connectedAccount') ||
      undefined;

    if (!this.clientSecret || !this.publishableKey) {
      this.router.navigate(['/checkout']);
      return;
    }

    this.shouldTrackPaymentSession = true;

    sessionStorage.setItem('clientSecret', this.clientSecret);
    sessionStorage.setItem('publishableKey', this.publishableKey);
    if (this.connectedAccount) {
      sessionStorage.setItem('connectedAccount', this.connectedAccount);
    }

    this.hydrateSummaryFromLastBasket();

    this.analytics.event('payment_page_opened', {
      currency: 'GBP',
      value: this.getPurchaseValueGBP(),
      order_type: this.getLastBasket()?.orderType || undefined,
      tenant_id: this.getLastBasket()?.tenantId || undefined,
      items: this.gaItemsFromLastBasket(),
    });
  }

  async ngAfterViewInit() {
    this.isInitialisingPayment = true;
    this.paymentReady = false;
    this.paymentInitMessage = 'Preparing secure payment...';

    this.analytics.event('payment_init_started', {
      currency: 'GBP',
      value: this.getPurchaseValueGBP(),
      order_type: this.getLastBasket()?.orderType || undefined,
      tenant_id: this.getLastBasket()?.tenantId || undefined,
    });

    try {
      await this.nextTick();

      if (!this.cardHost?.nativeElement) {
        await Promise.resolve();

        if (!this.cardHost?.nativeElement) {
          throw new Error('Card host element was not available.');
        }
      }

      this.paymentInitMessage = 'Connecting to secure payment provider...';

      // 1) Try CONNECTED scope
      let stripe = await this.loadStripeInstance(
        this.publishableKey,
        this.connectedAccount
          ? { stripeAccount: this.connectedAccount }
          : undefined,
      );

      if (stripe) {
        const res = await stripe.retrievePaymentIntent(this.clientSecret);

        if (!res.error) {
          this.stripe = stripe;
        }
      }

      // 2) Fallback: platform scope
      if (!this.stripe) {
        stripe = await this.loadStripeInstance(this.publishableKey);

        if (stripe) {
          const res = await stripe.retrievePaymentIntent(this.clientSecret);

          if (!res.error) {
            this.stripe = stripe;
          }
        }
      }

      if (!this.stripe) {
        throw new Error('Stripe could not be initialised.');
      }

      this.analytics.event('view_payment', {
        currency: 'GBP',
        value: this.getPurchaseValueGBP(),
        order_type: this.getLastBasket()?.orderType || undefined,
        tenant_id: this.getLastBasket()?.tenantId || undefined,
        has_connected_account: !!this.connectedAccount,
        items: this.gaItemsFromLastBasket(),
      });

      this.paymentInitMessage = 'Checking available payment methods...';

      await this.mountWalletButtons();

      if (this.walletAvailable && !this.prbHost?.nativeElement) {
        await this.nextTick();
      }

      this.paymentInitMessage = 'Loading secure card form...';

      this.mountCard();
    } catch (error) {
      console.error('[Payment] Initialisation failed:', error);

      this.analytics.event('payment_init_failed', {
        message: error instanceof Error ? error.message : String(error),
        currency: 'GBP',
        value: this.getPurchaseValueGBP(),
        order_type: this.getLastBasket()?.orderType || undefined,
        tenant_id: this.getLastBasket()?.tenantId || undefined,
      });

      this.cardError =
        'We couldn’t initialise the payment form. Please go back to checkout and try again.';

      this.isInitialisingPayment = false;
      this.paymentReady = false;
      this.cdr.detectChanges();
    }
  }

  private hydrateSummaryFromLastBasket() {
    const savedSummary = this.cartService.getPaymentSummary();

    if (savedSummary) {
      this.summaryItems = savedSummary.items;
      this.orderNotes = savedSummary.orderNotes;
      this.orderType = savedSummary.orderType;
      this.summaryDeliveryFee = savedSummary.deliveryFee;
      this.summaryServiceFee = savedSummary.serviceFee;
      this.summarySmallOrderFee = savedSummary.smallOrderFee;
      this.summaryTotal =
        savedSummary.total > 0
          ? savedSummary.total
          : this.totalAmount > 0
            ? this.totalAmount
            : 0;
      return;
    }

    const basket = this.getLastBasket();

    this.orderNotes = this.cartService.getOrderNotes();
    this.orderType =
      basket?.orderType === 'delivery' || basket?.deliveryType === 'delivery'
        ? 'delivery'
        : 'collection';

    this.summaryItems = this.mapSummaryItemsFromLastBasket(basket);

    this.summaryDeliveryFee = Number(
      basket?.deliveryFeeOriginalGross ??
        basket?.deliveryOriginalGross ??
        basket?.deliveryChargeGross ??
        basket?.deliveryFeeGross ??
        0,
    );

    this.summaryServiceFee = Number(
      basket?.customerAdminFee ?? basket?.serviceFee ?? 0,
    );

    this.summarySmallOrderFee = Number(basket?.smallOrderFee ?? 0);

    this.summaryTotal =
      this.totalAmount > 0 ? this.totalAmount : this.getPurchaseValueGBP();
  }

  private loadStripeInstance(
    publishableKey: string,
    options?: Parameters<typeof loadStripe>[1],
  ) {
    return loadStripe(publishableKey, options);
  }

  private mapSummaryItemsFromLastBasket(basket: any): CartItem[] {
    const items = Array.isArray(basket?.items) ? basket.items : [];

    return items.map((item: any, index: number) => {
      const quantity = Number(item?.itemQuantity ?? item?.quantity ?? 1) || 1;

      const explicitUnitPrice = Number(item?.itemBasePrice);
      const explicitLineTotal = Number(
        item?.itemTotalGrossOriginal ?? item?.itemTotalGross ?? item?.lineTotal,
      );

      const price =
        Number.isFinite(explicitUnitPrice) && explicitUnitPrice > 0
          ? explicitUnitPrice
          : Number.isFinite(explicitLineTotal) && explicitLineTotal > 0
            ? explicitLineTotal / quantity
            : 0;

      return {
        id: String(item?.lineId ?? item?.menuItemId ?? item?.id ?? index),
        itemId: Number(item?.menuItemId ?? item?.itemId ?? item?.id ?? 0),
        name: String(item?.itemName ?? item?.name ?? 'Item'),
        price: Number(price.toFixed(2)),
        quantity,
        imageUrl: '',
        options: Array.isArray(item?.options) ? item.options : [],
        extras: [],
      };
    });
  }

  private mountCard() {
    if (this.cardMounted) return;

    const host = this.cardHost?.nativeElement;

    if (!host) {
      this.cardError =
        'Payment form couldn’t be displayed. Please go back to checkout.';

      this.isInitialisingPayment = false;
      this.paymentReady = false;
      this.cdr.detectChanges();

      return;
    }

    const elements = this.stripe!.elements({ locale: 'en-GB' });

    const cardStyle = {
      base: {
        color: '#1a1f36',
        fontFamily:
          '"Inter", system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif',
        fontSmoothing: 'antialiased',
        fontSize: '16px',
        '::placeholder': { color: '#8a94a6' },
        iconColor: '#635bff',
      },
      invalid: {
        color: '#e4584c',
        iconColor: '#e4584c',
      },
    } as const;

    this.card = elements.create('card', {
      hidePostalCode: true,
      style: cardStyle,
      classes: {
        base: 'se-base',
        complete: 'se-complete',
        focus: 'se-focus',
        invalid: 'se-invalid',
      },
    });

    this.card.mount(host);
    this.cardMounted = true;

    this.card.on('ready', () => {
      this.isInitialisingPayment = false;
      this.paymentReady = true;
      this.paymentFormReadyAt = Date.now();

      this.analytics.event('payment_form_ready', {
        currency: 'GBP',
        value: this.getPurchaseValueGBP(),
        load_time_ms: this.paymentFormReadyAt - this.paymentPageOpenedAt,
        order_type: this.getLastBasket()?.orderType || undefined,
        tenant_id: this.getLastBasket()?.tenantId || undefined,
      });

      this.cdr.detectChanges();
    });

    this.card.on('focus', () => {
      if (this.hasTrackedCardFocus) return;

      this.hasTrackedCardFocus = true;

      this.analytics.event('payment_card_focused', {
        currency: 'GBP',
        value: this.getPurchaseValueGBP(),
        seconds_since_page_open: this.secondsSincePageOpen(),
        order_type: this.getLastBasket()?.orderType || undefined,
        tenant_id: this.getLastBasket()?.tenantId || undefined,
      });
    });

    this.card.on('change', (event) => {
      this.cardComplete = !!event.complete;
      this.cardError = event.error ? (event.error.message ?? '') : '';

      if (!this.hasTrackedCardStarted && !event.empty) {
        this.hasTrackedCardStarted = true;

        this.analytics.event('payment_card_started', {
          currency: 'GBP',
          value: this.getPurchaseValueGBP(),
          seconds_since_page_open: this.secondsSincePageOpen(),
          order_type: this.getLastBasket()?.orderType || undefined,
          tenant_id: this.getLastBasket()?.tenantId || undefined,
        });
      }

      if (!this.hasTrackedCardCompleted && event.complete) {
        this.hasTrackedCardCompleted = true;

        this.analytics.event('payment_card_completed', {
          currency: 'GBP',
          value: this.getPurchaseValueGBP(),
          seconds_since_page_open: this.secondsSincePageOpen(),
          order_type: this.getLastBasket()?.orderType || undefined,
          tenant_id: this.getLastBasket()?.tenantId || undefined,
        });
      }

      const validationMessage = event.error?.message ?? '';

      if (
        validationMessage &&
        validationMessage !== this.lastCardValidationError
      ) {
        this.lastCardValidationError = validationMessage;

        this.analytics.event('payment_card_validation_error', {
          message: validationMessage,
          code: event.error?.code ?? '',
          currency: 'GBP',
          value: this.getPurchaseValueGBP(),
          order_type: this.getLastBasket()?.orderType || undefined,
          tenant_id: this.getLastBasket()?.tenantId || undefined,
        });
      }

      if (!validationMessage) {
        this.lastCardValidationError = '';
      }

      this.cdr.detectChanges();
    });
  }

  private async mountWalletButtons() {
    if (!this.stripe) return;

    const elements = this.stripe.elements({ locale: 'en-GB' });
    const piRes = await this.stripe.retrievePaymentIntent(this.clientSecret);
    const piAmount = piRes.paymentIntent?.amount;

    const totalPence =
      typeof piAmount === 'number'
        ? piAmount
        : Math.round(this.totalAmount * 100);

    this.paymentRequest = this.stripe.paymentRequest({
      country: 'GB',
      currency: 'gbp',
      total: { label: 'Total', amount: totalPence },
      requestPayerName: true,
      requestPayerEmail: true,
    });

    const result = await this.paymentRequest.canMakePayment();

    this.walletAvailable = !!result;
    if (!this.walletAvailable) return;

    this.analytics.event('payment_wallet_available', {
      currency: 'GBP',
      value: this.getPurchaseValueGBP(),
      order_type: this.getLastBasket()?.orderType || undefined,
      tenant_id: this.getLastBasket()?.tenantId || undefined,
    });

    const walletType = (result as any)?.applePay
      ? 'apple_pay'
      : (result as any)?.googlePay
        ? 'google_pay'
        : 'wallet';

    this.analytics.event('payment_wallet_type_available', {
      payment_type: walletType,
      currency: 'GBP',
      value: this.getPurchaseValueGBP(),
      order_type: this.getLastBasket()?.orderType || undefined,
      tenant_id: this.getLastBasket()?.tenantId || undefined,
    });

    // NOW the host exists (because it’s no longer hidden / or at least it's in the DOM)
    const host = this.prbHost?.nativeElement;
    if (!host) {
      console.warn('[Wallet] prbHost missing even though walletAvailable=true');
      return;
    }

    this.prButton = elements.create('paymentRequestButton', {
      paymentRequest: this.paymentRequest,
      style: {
        paymentRequestButton: { type: 'default' },
      },
    });

    this.prButton.mount(host);

    this.paymentRequest.on('paymentmethod', async (ev) => {
      if (!this.stripe) return;

      this.hasClickedPay = true;

      this.analytics.event('payment_pay_clicked', {
        currency: 'GBP',
        value: this.getPurchaseValueGBP(),
        payment_type: walletType,
        card_completed: false,
        seconds_since_page_open: this.secondsSincePageOpen(),
        order_type: this.getLastBasket()?.orderType || undefined,
        tenant_id: this.getLastBasket()?.tenantId || undefined,
        items: this.gaItemsFromLastBasket(),
      });

      this.analytics.event('add_payment_info', {
        currency: 'GBP',
        value: this.getPurchaseValueGBP(),
        payment_type: walletType,
        items: this.gaItemsFromLastBasket(),
      });

      const { paymentIntent, error } = await this.stripe.confirmCardPayment(
        this.clientSecret,
        { payment_method: ev.paymentMethod.id },
        { handleActions: false },
      );

      if (error) {
        ev.complete('fail');
        this.cardError = error.message ?? 'Wallet payment failed.';

        this.analytics.event('payment_failure', {
          payment_type: walletType,
          message: error.message ?? '',
        });

        return;
      }

      ev.complete('success');

      // If 3DS or further action required
      if (paymentIntent?.status === 'requires_action') {
        const { error: actionError, paymentIntent: updatedPI } =
          await this.stripe.confirmCardPayment(this.clientSecret);

        if (actionError) {
          this.cardError = actionError.message ?? 'Authentication failed.';

          this.analytics.event('payment_failure', {
            payment_type: walletType,
            message: actionError.message ?? '',
          });

          return;
        }

        if (
          updatedPI?.status === 'succeeded' ||
          updatedPI?.status === 'requires_capture'
        ) {
          this.hasSuccessfulPayment = true;
          this.trackPurchase(updatedPI?.id ?? paymentIntent?.id);
          this.clearCartAfterSuccessfulPayment();
          this.router.navigate(['/order-confirmed']);
        }
        return;
      }

      if (
        paymentIntent?.status === 'succeeded' ||
        paymentIntent?.status === 'requires_capture'
      ) {
        this.hasSuccessfulPayment = true;
        this.trackPurchase(paymentIntent?.id);
        this.clearCartAfterSuccessfulPayment();
        this.router.navigate(['/order-confirmed']);
      }
    });
  }

  async pay() {
    if (!this.stripe || !this.card || !this.paymentReady || this.isPaying)
      return;

    this.hasClickedPay = true;

    this.analytics.event('payment_pay_clicked', {
      currency: 'GBP',
      value: this.getPurchaseValueGBP(),
      payment_type: 'card',
      card_completed: this.cardComplete,
      seconds_since_page_open: this.secondsSincePageOpen(),
      order_type: this.getLastBasket()?.orderType || undefined,
      tenant_id: this.getLastBasket()?.tenantId || undefined,
      items: this.gaItemsFromLastBasket(),
    });

    this.analytics.event('add_payment_info', {
      currency: 'GBP',
      value: this.getPurchaseValueGBP(),
      payment_type: 'card',
      items: this.gaItemsFromLastBasket(),
    });

    this.isPaying = true;
    this.cardError = '';

    try {
      const { paymentIntent, error } = await this.stripe.confirmCardPayment(
        this.clientSecret,
        { payment_method: { card: this.card } },
      );

      if (error) {
        this.cardError =
          error.message ?? 'Your payment could not be processed.';

        this.analytics.event('payment_failure', {
          payment_type: 'card',
          message: error.message ?? '',
        });

        return;
      }

      if (
        paymentIntent?.status === 'succeeded' ||
        paymentIntent?.status === 'requires_capture'
      ) {
        this.hasSuccessfulPayment = true;
        this.trackPurchase(paymentIntent?.id);
        this.clearCartAfterSuccessfulPayment();
        this.router.navigate(['/order-confirmed']);
      }
    } finally {
      this.isPaying = false;
    }
  }

  private clearCartAfterSuccessfulPayment() {
    this.cartService.clearCart();
    this.cartService.clearPaymentSummary();

    try {
      sessionStorage.removeItem('waitron.orderNotes');
      sessionStorage.removeItem('waitron.orderType');
    } catch {}
  }

  ngOnDestroy() {
    this.trackPaymentAbandon(this.leaveReason);

    try {
      this.prButton?.unmount();
    } catch {}

    try {
      if (this.card) {
        this.card.unmount();
        this.cardMounted = false;
      }
    } catch {}
  }

  private async nextTick() {
    await Promise.resolve();
  }

  private getLastBasket(): any | null {
    try {
      const raw = sessionStorage.getItem('lastBasket');
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  private gaItemsFromLastBasket() {
    const basket = this.getLastBasket();
    const items = basket?.items || [];

    return items.map((i: any) => {
      const qty = Number(i.itemQuantity ?? 1) || 1;

      const baseUnit = Number(i.itemBasePrice);
      const lineTotal = Number(i.itemTotalGrossOriginal);

      const unitPrice =
        Number.isFinite(baseUnit) && baseUnit > 0
          ? baseUnit
          : Number.isFinite(lineTotal) && lineTotal > 0
            ? lineTotal / qty
            : 0;

      return {
        item_id: String(i.menuItemId ?? ''),
        item_name: String(i.itemName ?? ''),
        price: Number(unitPrice.toFixed(2)),
        quantity: qty,

        // Optional extras (if you ever add them)
        item_variant: undefined,
        item_category: undefined,
      };
    });
  }

  private getPurchaseValueGBP(): number {
    const basket = this.getLastBasket();

    const v = Number(basket?.orderTotalCurrentGross);
    if (Number.isFinite(v) && v >= 0) return v;

    const itemsTotal = Array.isArray(basket?.items)
      ? basket.items.reduce((sum: number, item: any) => {
          const quantity =
            Number(item?.itemQuantity ?? item?.quantity ?? 1) || 1;
          const unitPrice =
            Number(item?.itemBasePrice ?? item?.price ?? 0) || 0;
          const lineTotal =
            Number(item?.itemTotalGrossOriginal ?? item?.itemTotalGross) || 0;

          return sum + (lineTotal > 0 ? lineTotal : unitPrice * quantity);
        }, 0)
      : 0;

    const deliveryFee =
      Number(
        basket?.deliveryFeeOriginalGross ??
          basket?.deliveryOriginalGross ??
          basket?.deliveryChargeGross ??
          basket?.deliveryFeeGross,
      ) || 0;
    const adminFee = Number(basket?.customerAdminFee) || 0;
    const smallOrderFee = Number(basket?.smallOrderFee) || 0;
    const computedBasketTotal =
      itemsTotal + deliveryFee + adminFee + smallOrderFee;

    if (computedBasketTotal > 0) {
      return Number(computedBasketTotal.toFixed(2));
    }

    const fallbackTotal = Number(this.totalAmount);
    if (Number.isFinite(fallbackTotal) && fallbackTotal >= 0) {
      return Number(fallbackTotal.toFixed(2));
    }

    return 0;
  }

  private getTransactionId(paymentIntentId?: string): string {
    const basket = this.getLastBasket();
    return (
      String(basket?.publicOrderCode || '') ||
      String(basket?.orderId || '') ||
      String(paymentIntentId || '')
    );
  }

  private hasSentPurchase(txId: string): boolean {
    try {
      const key = `waitron.purchaseSent.${txId}`;
      if (sessionStorage.getItem(key)) return true;
      sessionStorage.setItem(key, '1');
      return false;
    } catch {
      return false;
    }
  }

  private trackPurchase(paymentIntentId?: string) {
    const txId = this.getTransactionId(paymentIntentId);
    if (!txId) return;
    if (this.hasSentPurchase(txId)) return;

    const value = this.getPurchaseValueGBP();
    this.analytics.event('purchase', {
      transaction_id: txId,
      value,
      currency: 'GBP',
      items: this.gaItemsFromLastBasket(),
      order_type: this.getLastBasket()?.orderType || undefined,
      tenant_id: this.getLastBasket()?.tenantId || undefined,
    });
  }

  backToCheckout() {
    this.leaveReason = 'back_to_checkout';

    this.analytics.event('payment_back_clicked', {
      currency: 'GBP',
      value: this.getPurchaseValueGBP(),
      card_focused: this.hasTrackedCardFocus,
      card_started: this.hasTrackedCardStarted,
      card_completed: this.hasTrackedCardCompleted,
      pay_clicked: this.hasClickedPay,
      seconds_since_page_open: this.secondsSincePageOpen(),
      order_type: this.getLastBasket()?.orderType || undefined,
      tenant_id: this.getLastBasket()?.tenantId || undefined,
    });

    this.trackPaymentAbandon('back_to_checkout');

    this.router.navigate(['/checkout']);
  }

  @HostListener('window:beforeunload')
  onBeforeUnload() {
    this.leaveReason = 'browser_close_or_refresh';
    this.trackPaymentAbandon('browser_close_or_refresh');
  }

  @HostListener('window:pagehide')
  onPageHide() {
    this.leaveReason = 'browser_close_or_refresh';
    this.trackPaymentAbandon('browser_close_or_refresh');
  }

  private trackPaymentAbandon(
    reason: 'unknown' | 'back_to_checkout' | 'browser_close_or_refresh',
  ) {
    if (!this.shouldTrackPaymentSession) return;
    if (this.hasTrackedAbandon) return;
    if (this.hasSuccessfulPayment) return;

    this.hasTrackedAbandon = true;

    this.analytics.event('payment_page_abandoned', {
      reason,
      currency: 'GBP',
      value: this.getPurchaseValueGBP(),
      payment_ready: this.paymentReady,
      card_focused: this.hasTrackedCardFocus,
      card_started: this.hasTrackedCardStarted,
      card_completed: this.hasTrackedCardCompleted,
      pay_clicked: this.hasClickedPay,
      wallet_available: this.walletAvailable,
      seconds_since_page_open: this.secondsSincePageOpen(),
      order_type: this.getLastBasket()?.orderType || undefined,
      tenant_id: this.getLastBasket()?.tenantId || undefined,
      items: this.gaItemsFromLastBasket(),
    });
  }

  private secondsSincePageOpen(): number {
    return Math.round((Date.now() - this.paymentPageOpenedAt) / 1000);
  }
}
